import { useCallback, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Error } from 'components/molecules/ErrorList';
import { Box, Typography } from '@mui/material';
import { apiClient } from 'config/api';
import { Alert, Button, StrongModalText } from '@atoms';
import { CSV_LINE_ERRORS_MESSAGES, PERMISSIONS } from '@globalConstants';
import {
  useCustomerId,
  usePartnerCustomers,
  usePermissions,
  useSelectedCompanyDetails,
  useUser,
} from '@hooks';
import { BoxContainer } from '@layouts';
import { Dropzone, ErrorList } from '@molecules';
import { parseAmount } from '@utils';
import styles from '../styles';

interface UploadFileProps {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
}

/**
 * Upload file component for gift cards dispersion. It includes a dropzone to select the file, a button to submit it and modals to show the confirmation, success or error messages.
 */
const UploadFile = ({ isLoading, setLoading }: UploadFileProps) => {
  const [dispersionSuccess, setDispersionSuccess] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [error, setError] = useState({ title: '', description: '' });
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [fileErrorDetails, setFileErrorDetails] = useState<Error[]>([]);
  const { user } = useUser();
  const { customerId } = useCustomerId();
  const { data: companyData } = useSelectedCompanyDetails();
  const companyName = companyData?.description;
  const [confirmDispersion, setConfirmDispersion] = useState({
    openModal: false,
    prospectId: null,
    total: null,
    count: null,
    totalAmount: null,
  });
  const { isLoading: isCustomersLoading, error: customersError } = usePartnerCustomers();
  const canCreateNamelessCards = usePermissions([PERMISSIONS.createGiftCards]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasSelectedFile = files.length > 0;
  const hasFileLineErrors = fileErrorDetails.length > 0;

  /**
   * Handle the selection of a file
   */
  const handleSelectFiles = useCallback(
    (files: File[]) => {
      if (hasFileLineErrors) {
        setFileErrorDetails([]);
      }

      setFiles(files);
    },
    [hasFileLineErrors]
  );

  /**
   * validation to disable or not send file button
   */
  const disableButton = () => {
    if (
      isLoading ||
      hasFileLineErrors ||
      isCustomersLoading ||
      customersError ||
      !canCreateNamelessCards
    )
      return true;

    if (user?.isInternal) {
      if (!customerId) return true;
    } else {
      if (!companyData) return true;
    }

    return !hasSelectedFile;
  };

  /**
   * Handle dispersion confirmation
   * @param {boolean} confirm Whether the user confirms of cancels the dispersion
   */
  const handleConfirmation = async (confirm: boolean) => {
    setLoading(true);
    try {
      await apiClient.put('/v4/nameless-cards/confirm', {
        prospectId: confirmDispersion.prospectId,
        confirm,
      });
      if (confirm) {
        setDispersionSuccess(true);
        setFiles([]);
      }
    } catch (error) {
      if (confirm) {
        if (
          isAxiosError(error) &&
          error.response?.status === 404 &&
          error.response.data?.errors?.[0]?.message === 'Key not found'
        ) {
          setError({
            title: 'DISPERSIONS:ERRORS:EXPIRED_TITLE',
            description: 'DISPERSIONS:ERRORS:EXPIRED_DESCRIPTION',
          });
        } else {
          setError({
            title: 'DISPERSIONS:ERRORS:CONFIRMATION_ERROR_TITLE',
            description: 'DISPERSIONS:ERRORS:CONFIRMATION_ERROR_DESCRIPTION',
          });
        }
        setErrorOpen(true);
      }
    }
    setConfirmDispersion({
      prospectId: null,
      openModal: false,
      total: null,
      count: null,
      totalAmount: null,
    });
    setLoading(false);
  };

  /**
   * send file to be
   */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('customerId', String(customerId));

      const url = '/v4/nameless-cards/bulk-create';
      const body = formData;

      const {
        data: { prospectId, total, count, totalAmount },
      } = await apiClient.post(url, body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setConfirmDispersion({ openModal: true, prospectId, total, count, totalAmount });
    } catch (error) {
      if (isAxiosError(error)) {
        const response = error.response;
        if (response?.status === 400) {
          setErrorOpen(true);
          if (
            response?.data.code === 'csv_line_errors' ||
            response?.data.code === 'file_line_errors'
          ) {
            const fileErrors = response.data.data?.data ?? response.data.data;
            setFileErrorDetails(fileErrors);
            setError({
              title: 'DISPERSIONS:ERRORS:FILE_CONTAINS_ERRORS_TITLE',
              description: 'DISPERSIONS:ERRORS:FILE_CONTAINS_ERRORS_DESCRIPTION',
            });
          } else if (response.data.code === 'csv_record_inconsistent_columns') {
            setError({
              title: 'COMMON:VALIDATIONS:INCONSISTENT_COLUMNS_TITLE',
              description: t('COMMON:VALIDATIONS:INCONSISTENT_COLUMNS_DESCRIPTION', {
                line: response.data.data.line,
              }),
            });
          } else {
            const code = response.data?.errors[0]?.message;
            switch (code) {
              case 'Filename is not accepted.':
                setError({
                  title: 'DISPERSIONS:ERRORS:INVALID_FILE_NAME_TITLE',
                  description: 'DISPERSIONS:ERRORS:INVALID_FILE_NAME_DESCRIPTION',
                });
                break;
              case 'Account balance is less than dispersion amount.':
              case 'Account available balance is less than dispersion amount.':
                setError({
                  title: 'DISPERSIONS:ERRORS:INSUFFICIENT_BALANCE_TITLE',
                  description: 'DISPERSIONS:ERRORS:INSUFFICIENT_BALANCE_DESCRIPTION',
                });
                break;
              case 'Area balance is less than dispersion amount.':
                setError({
                  title: 'DISPERSIONS:ERRORS:INSUFFICIENT_BALANCE_TITLE',
                  description: 'DISPERSIONS:ERRORS:NOT_ENOUGH_BALANCE_AREA',
                });
                break;
              case 'Another dispersion is already processing': {
                setError({
                  title: 'DISPERSIONS:ERRORS:ALREADY_PROCESSING_TITLE',
                  description: 'DISPERSIONS:ERRORS:ALREADY_PROCESSING_DESCRIPTION',
                });
                break;
              }
              case 'Another dispersion is scheduled at the same time': {
                setError({
                  title: 'DISPERSIONS:ERRORS:DUPLICATE_TIME_FOR_SCHEDULED_TITLE',
                  description: 'DISPERSIONS:ERRORS:DUPLICATE_TIME_FOR_SCHEDULED_DESCRIPTION',
                });
                break;
              }
              default:
                setError({
                  title: 'COMMON:ERRORS:GENERIC_ERROR_TITLE',
                  description: 'DISPERSIONS:ERRORS:DEFAULT_BAD_REQUEST_ERROR',
                });
                break;
            }
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <BoxContainer>
      <Box sx={styles.paperHead}>
        <Typography variant='h4'>{t('GIFT_CARDS:DROPZONE_TITLE')}</Typography>
      </Box>
      <Dropzone
        files={files}
        setFiles={setFiles}
        disabled={isLoading}
        handleFilesSelection={handleSelectFiles}
      />
      <Button
        text={'COMMON:FILE_UPLOADS:SEND_FILE_BUTTON'}
        variant='contained'
        color='primary'
        onClick={handleSubmit}
        disabled={disableButton()}
        isLoading={isLoading}
        size='large'
      />
      <Alert
        variant='warning'
        modalVisible={confirmDispersion.openModal}
        handleClose={() => handleConfirmation(false)}
        handleSubmit={() => handleConfirmation(true)}
        closeButtonText='COMMON:CANCEL'
        title={t('DISPERSIONS:MODAL_TITLE')}
        submitLoading={isLoading}
        disabled={disableButton()}
        content={
          <Typography color='gray.90'>
            <Trans
              i18nKey={'GIFT_CARDS:CONFIRMATION_MODAL'}
              components={{ bold: <StrongModalText /> }}
              values={{
                total: confirmDispersion.total,
                count: confirmDispersion.total,
                companyName,
                totalAmount: parseAmount(confirmDispersion.totalAmount ?? 0),
              }}
            />
          </Typography>
        }
      />
      <Alert
        modalVisible={dispersionSuccess}
        handleClose={() => setDispersionSuccess(false)}
        variant='info'
        title={'DISPERSIONS:DISPERSIONS_SUCCESS_TITLE_INSTANT'}
        description={'GIFT_CARDS:SUCCESS'}
        confirmButtonText={'GIFT_CARDS:GO_TO_LIST'}
        handleSubmit={() => {
          navigate('/tarjetas-innominadas/mis-altas');
        }}
        customStyles={{ description: { whiteSpace: 'pre-line' } }}
        closeButtonText={'COMMON:CLOSE'}
      />
      <Alert
        modalVisible={errorOpen}
        handleClose={() => setErrorOpen(false)}
        description={error.description}
        title={error.title}
        widePaper={hasFileLineErrors}
        content={
          hasFileLineErrors && (
            <ErrorList
              errors={fileErrorDetails}
              title={'DISPERSIONS:CSV_ERRORS_TITLE'}
              getMessages={CSV_LINE_ERRORS_MESSAGES}
            />
          )
        }
      />
    </BoxContainer>
  );
};

export default UploadFile;
